// @flow
import { createContext, FunctionalComponent, h } from 'preact';
import { useContext, useReducer } from 'preact/hooks';
import type {
  LenderDetailsType,
  CheckEligibilityResponseType,
  GetEmiDetailsResponseType,
  PreCheckEligibilityResponseType,
} from '../../api/controllers/salesController';

type SalesStateType = {
  mobile?: string,
  amount?: string,
  currentUrl: string,
  mobileAmountConfig?: {
    showEditForMobile: boolean,
    showEditForAmount: boolean,
  },
  merchantData: {
    merchantURL?: string,
    merchantName?: string,
    merchantLogo?: string,
    merchantAgentId?: string,
    merchantKey?: string,
    theme: any,
    postMsgMerchantOrigin?: string,
    type: string,
  },
  bottomSheetConfig?: {
    showOverlay: boolean,
    moduleName: string | null,
    overlayHeight?: string,
    routeOnCompletion?: boolean,
  },
  bottomSheetData?: {
    mainTitle: string,
    subTitle: string,
    primaryText: string,
    secondaryText: string,
    btnName: string,
    textColor: string,
  },
  showInterstitial: boolean,
  showOtpScreen: boolean,
  selectedModule: string,
  selectedLenderDetails?: LenderDetailsType,
  selectedLenderId?: string,
  checkEligibilityResponse?: CheckEligibilityResponseType,
  preCheckEligibilityResponse?: PreCheckEligibilityResponseType,
  isPreCheckEligibilityDone: boolean,
  preCheckEligibilityMobileNumber: string,
  getEmiDetailsResponse?: GetEmiDetailsResponseType,
  ntbSmsData: {
    ntbLink: string,
    whatsAppLink: string,
  },
  routeDetails?: {
    component: string,
    componentName: string,
    path: string,
    template: string,
    theme: string,
  },
};
const initialState: SalesStateType = {
  mobileAmountConfig: {
    showEditForMobile: true,
    showEditForAmount: true,
  },
  bottomSheetConfig: {
    showOverlay: false,
    moduleName: null,
    overlayHeight: null,
    routeOnCompletion: true,
  },
  showInterstitial: false,
  merchantData: {},
  mobile: '',
  currentUrl: '',
};

type Action = {
  type: 'STORE_STATE' | 'RESET_STORE' | 'SEND_MESSAGE' | 'SHOW_SALES_APP',
  payload?: SalesStateType,
};

type Dispatch = (action: Action) => void;

const SalesAppContext = createContext(initialState);

const SalesDispatchContext = createContext({
  salesDispatch: () => {},
});

const reducer = (state: SalesStateType, action: Action): SalesStateType => {
  switch (action.type) {
    case 'STORE_STATE':
      return {
        ...state,
        ...action.payload,
      };
    case 'SHOW_SALES_APP': {
      const { merchantData } = action.payload || {};
      return {
        ...state,
        merchantData: {
          merchantURL: merchantData.merchantURL,
          merchantKey: merchantData.merchantKey,
          merchantName: merchantData.merchantName,
          merchantLogo: merchantData.merchantLogo,
          merchantAgentId: merchantData.merchantAgentId,
          postMsgMerchantOrigin: merchantData.postMsgMerchantOrigin,
          theme: merchantData.theme,
          type: merchantData.type,
          authToken: merchantData.authToken,
        },
      };
    }
    case 'SEND_MESSAGE':
      const data = action.payload;
      if (state.merchantData.postMsgMerchantOrigin && data) {
        const sendMessage = (messageData) => {
          return window.parent.postMessage(
            JSON.stringify(messageData),
            state.merchantData.postMsgMerchantOrigin,
          );
        };

        switch (data.actionName) {
          case 'closeSAApp':
            sendMessage({
              actionName: data.actionName,
              payload: data.payload,
            });
            break;
          case 'getAuthToken':
            sendMessage({
              actionName: data.actionName,
              payload: data.payload,
            });
            break;
        }
      }

      return state;
    case 'RESET_STORE':
      return initialState;
    default:
      return state;
  }
};

const SalesAppProvider: FunctionalComponent = ({ children, routeDetails }) => {
  const [salesState, salesDispatch] = useReducer(reducer, initialState);
  const value = { ...salesState, routeDetails: routeDetails };

  const dispatchValue = salesDispatch;

  return (
    <SalesAppContext.Provider value={value}>
      <SalesDispatchContext.Provider value={dispatchValue}>
        {children}
      </SalesDispatchContext.Provider>
    </SalesAppContext.Provider>
  );
};

const useSalesContext = () => {
  const context = useContext(SalesAppContext);
  return context;
};

const useSalesDispatchContext = () => {
  const context = useContext(SalesDispatchContext);
  return context;
};

export { SalesAppProvider, useSalesContext, useSalesDispatchContext };



// WEBPACK FOOTER //
// ./storeProviders/salesAppStore/index.js